import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { Checkbox, useToast } from '@morressier/alembic';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAllUADReports, fetchDownloadUADReport } from 'src/api/queries';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import Loading from 'src/pages/Status/Loading';
import ReportEntriesList from './ReportEntriesList';
import AcknowledgeModal from './AcknowledgeReports';
import { IUADReportWithDetails } from '@publishing-engine/domain/models';
import { NoRecordFound } from 'src/pages/ReviewList/EmptyState';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/pro-regular-svg-icons';
import AcknowledgeComments from './AcknowledgeComment';
import { formatDateAndTime } from 'src/utils/date';
import ProtectedActionButton from 'src/components/ProtectedAction/Button';

const UADTab = () => {
  const { workflowResponse } = useSubmissionData();
  const toast = useToast();
  const submissionContextId = workflowResponse?.submissionContextId;
  const currentState = workflowResponse?.workflowData?.currentState;
  const submissionId = workflowResponse?.submissionId;
  const isUADDetected = workflowResponse?.isUADDetected;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [downloadReportsLoading, setDownloadReportsLoading] =
    useState<boolean>(false);
  const { data: UADReportsData, isLoading } = useQuery({
    queryKey: ['uad-reports', { submissionId }],
    queryFn: () => fetchAllUADReports(submissionId),
    retry: 0
  });

  const reports = UADReportsData?.result || [];
  const { acknowledgedComments, remainingComments } = reports.reduce<{
    acknowledgedComments: IUADReportWithDetails[];
    remainingComments: IUADReportWithDetails[];
  }>(
    (acc, report) => {
      (report.isAcknowledged
        ? acc.acknowledgedComments
        : acc.remainingComments
      ).push(report);

      return acc;
    },
    {
      acknowledgedComments: [],
      remainingComments: []
    }
  );

  const onClose = () => {
    setIsOpen(false);
  };

  const selectAllChecks = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedReports(remainingComments.map((report: any) => report.id));
    } else {
      setSelectedReports([]);
    }
  };
  const DownloadReport = async () => {
    setDownloadReportsLoading(true);
    try {
      const ReportData = await fetchDownloadUADReport(submissionId);
      const disposition = ReportData.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : 'report.pdf';
      const blob = new Blob([ReportData.data], {
        type: 'application/pdf'
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error While Downloading Report',
        description: 'Please try again later!',
        isClosable: true
      });
    } finally {
      setDownloadReportsLoading(false);
    }
  };
  const handleSelectReport = (reportId: string, isChecked: boolean) => {
    setSelectedReports(prev => {
      if (isChecked) {
        return [...prev, reportId];
      }
      return prev.filter(id => id !== reportId);
    });
  };

  if (isLoading) {
    return <Loading />;
  }
  if (isUADDetected && !reports.length) {
    return (
      <NoRecordFound
        title="Generating Reports"
        subTitle="Anomaly activity detected. Reports will be available soon."
      />
    );
  }
  if (!reports.length) {
    return (
      <NoRecordFound
        title="No Reports Available"
        subTitle="No reports have been generated for this submission yet."
      />
    );
  }
  return (
    <>
      <Box>
        <Box>
          <Flex justifyContent={'space-between'}>
            <Box mx={3}>
              {Boolean(remainingComments.length) && (
                <Flex alignItems={'center'} gap={3}>
                  <Checkbox
                    id="select-all"
                    name="select-all"
                    ariaLabel="Select All Reports"
                    lineHeight={0}
                    isChecked={
                      reports.length > 0 &&
                      selectedReports.length === remainingComments.length
                    }
                    disabled={!remainingComments.length}
                    onChange={selectAllChecks}
                  >
                    Select all Reports
                  </Checkbox>
                  <ProtectedActionButton
                    relation="decision_maker"
                    entityType="journal_phase"
                    entityId={`${submissionContextId}_${currentState}`}
                    size="context"
                    as="button"
                    variant="primary"
                    textStyle={'textContext'}
                    onClick={() => setIsOpen(true)}
                    disabled={
                      !selectedReports.length || !remainingComments.length
                    }
                  >
                    Acknowledge
                  </ProtectedActionButton>
                </Flex>
              )}
            </Box>
            <ProtectedActionButton
              relation="decision_maker"
              entityType="journal_phase"
              entityId={`${submissionContextId}_${currentState}`}
              size="context"
              variant="primary"
              as="text"
              textStyle={'textContext'}
              onClick={DownloadReport}
              disabled={downloadReportsLoading}
            >
              <FontAwesomeIcon icon={faCloudArrowDown} /> Download Report
              {downloadReportsLoading && <Spinner />}
            </ProtectedActionButton>
          </Flex>
        </Box>
        <Box my={2}></Box>
        <Box>
          {remainingComments.map((details: IUADReportWithDetails) => {
            const GeneratedDate = details?.date
              ? formatDateAndTime(details.date)
              : '--';
            return (
              <>
                {' '}
                <Box
                  key={details?.id}
                  borderWidth="1px"
                  mt={3}
                  backgroundColor={'surfaceSecondaryFill'}
                >
                  <Flex
                    flexDirection={'column'}
                    justifyContent={'center'}
                    py={4}
                    px={4}
                    bg={'white'}
                    width={'100%'}
                  >
                    <Checkbox
                      id={details?.id}
                      name={details?.name}
                      ariaLabel={details?.name}
                      lineHeight={0}
                      isChecked={selectedReports.includes(details?.id)}
                      onChange={e =>
                        handleSelectReport(details?.id, e.target.checked)
                      }
                    >
                      <Box>
                        <Text textStyle={'headingItem'} as={'span'}>
                          {details?.name}
                        </Text>

                        <Text as={'span'} ml={2}>
                          ({details?.uadEntries?.length} activities)
                        </Text>
                      </Box>

                      <Text textStyle={'textContext'}>
                        Generated on {GeneratedDate}
                      </Text>
                    </Checkbox>
                  </Flex>

                  <Box>
                    <ReportEntriesList entries={details?.uadEntries} />
                  </Box>
                </Box>
              </>
            );
          })}
        </Box>

        <AcknowledgeComments acknowledgedComments={acknowledgedComments} />
        {isOpen && (
          <AcknowledgeModal
            isOpen={isOpen}
            submissionId={submissionId}
            reportIds={selectedReports}
            onClose={onClose}
          />
        )}
      </Box>
    </>
  );
};

export default UADTab;
